/**
 * Shared storage constants and utilities for cross-app language synchronization.
 * All apps in the monorepo should use these constants to ensure language
 * preferences are synced across the entire portal.
 */

export const LANGUAGE_STORAGE_KEY = "language";

/**
 * Get the currently stored language preference.
 * Returns null if no preference is stored or localStorage is unavailable.
 */
export const getStoredLanguage = (): string | null => {
	if (typeof globalThis.window === "undefined" || typeof globalThis.localStorage === "undefined") {
		return null;
	}

	try {
		return globalThis.localStorage.getItem(LANGUAGE_STORAGE_KEY);
	} catch {
		return null;
	}
};

/**
 * Set the language preference in localStorage.
 * Triggers storage events in other tabs/windows for synchronization.
 */
export const setStoredLanguage = (language: string): void => {
	if (typeof globalThis.window === "undefined" || typeof globalThis.localStorage === "undefined") {
		return;
	}

	try {
		globalThis.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
	} catch {
		// Ignore localStorage access errors
	}
};

/**
 * Listen for language changes from other tabs/windows and invoke callback.
 * Returns an unsubscribe function to remove the listener.
 *
 * @example
 * const unsubscribe = onLanguageStorageChange((newLanguage) => {
 *   console.log('Language changed to:', newLanguage);
 * });
 * // Later: unsubscribe();
 */
export const onLanguageStorageChange = (callback: (language: string | null) => void): (() => void) => {
	if (typeof globalThis.addEventListener === "undefined") {
		return () => {};
	}

	const handleStorageChange = (event: StorageEvent) => {
		if (event.key === LANGUAGE_STORAGE_KEY && event.newValue !== null) {
			callback(event.newValue);
		}
	};

	globalThis.addEventListener("storage", handleStorageChange);

	return () => {
		globalThis.removeEventListener("storage", handleStorageChange);
	};
};
